/**
 * Spec Phase 4 Slice 4D — PaperSurface.
 *
 * Center-pane backdrop (zinc-100) + the paper page itself
 * (max-width 720px, white, soft shadow). Renders the active
 * section's heading + paragraphs.
 *
 * Auto-generated sections (reference_signs_list, claims_mirror)
 * delegate to ``AutoGeneratedSection`` (Phase 7 Slice 7D), which
 * fetches the live render via the spec store + section render API.
 *
 * Phase 8 Slice 8C: when a transient section-verify run has
 * issues for the active section, a section-level banner above
 * the paragraphs surfaces the count, and per-paragraph badges
 * show the issue inline. Issues live in the store
 * (sectionVerifyIssues) — wiped on regenerate / re-verify /
 * page reload.
 *
 * Empty drafted sections (no paragraphs yet) surface a hint pointing
 * the drafter at the toolbar's Generate button.
 */
import React, { useState } from 'react';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import type { SpecParagraph, SpecSectionRow } from '@/api/client';
import AutoGeneratedSection from './AutoGeneratedSection';
import ParagraphBlock, { isSectionEditable } from './ParagraphBlock';
import CrossReferenceForm from './CrossReferenceForm';
import { bucketVerifyIssues } from './verifyIssues';

interface Props {
  section: SpecSectionRow | null;
  /** BUG-045 — when true, the surface renders only the inner
   * paper block (no backdrop, no own scroll). PaperEditor
   * stacks one block per section inside its own scroll
   * container; the empty/standalone case still wraps with the
   * backdrop so direct usage outside the editor keeps working. */
  embedded?: boolean;
}

export default function PaperSurface({
  section, embedded = false,
}: Props): JSX.Element {
  const paragraphsBySection = useSpecStore((s) => s.paragraphsBySection);
  const sectionVerifyIssues = useSpecStore((s) => s.sectionVerifyIssues);
  // BUG-037 — US has no industrial-applicability requirement. The
  // backend's deterministic skip already inserts "Not applicable to
  // US practice." when the drafter clicks Generate, but the empty-
  // state UI still showed a generic "Click Generate" CTA which is
  // confusing for a section that's effectively a no-op. Surface the
  // skip rationale inline so drafters can either accept (clicking
  // Generate persists the placeholder) or skip the section entirely.
  const jurisdiction = useSpecStore((s) =>
    s.specDocument?.jurisdiction || null,
  );
  const matterId = useSpecStore((s) => s.matterId);
  const sectionVerifyScore = useSpecStore((s) => s.sectionVerifyScore);
  // BUG-044 — cross-reference manual-mode toggle. Per-section
  // local state; resets when the section changes (the key on
  // PaperSurface inside the continuous-scroll PaperEditor uses
  // section.id, so the component remounts cleanly). Declared
  // above the early `if (!section) return` so the hook runs in
  // the same order every render — react-hooks/rules-of-hooks
  // promotes the conditional-call pattern to a build error in
  // production.
  const [crossRefManual, setCrossRefManual] = useState(false);
  const paragraphs: SpecParagraph[] = section
    ? paragraphsBySection[section.id] ?? []
    : [];
  const issues = section ? sectionVerifyIssues[section.id] ?? [] : [];
  const score = section ? sectionVerifyScore[section.id] ?? null : null;
  const { issuesByPara, sectionScopedIssues } = bucketVerifyIssues(issues, paragraphs);

  if (!section) {
    return (
      <div
        className="s6-paper-backdrop flex flex-1 items-center justify-center"
        data-testid="spec-paper-empty-state"
      >
        <div className="text-sm text-zinc-500">
          Select a section in the left rail to start.
        </div>
      </div>
    );
  }

  const seq = String(section.sequence_num).padStart(2, '0');
  const isCrossRefSection = section.section_kind === 'cross_reference';

  // BUG-043 — title section presents as a centred ALL CAPS line,
  // not as an eyebrow + heading + numbered paragraph. The left
  // nav already labels "Section 01 / Title"; the body should
  // render only the title text in patent-document style. Other
  // sections keep the standard eyebrow + heading.
  const isTitleSection = section.section_kind === 'title';

  if (isTitleSection) {
    // BUG-055 — Title section was rendering paragraphs as a static
    // <h1>, which made the title text uneditable. Route through
    // ParagraphBlock with variant='title' so the patent-title
    // styling (centred / uppercase / weight-600 via s6-title-line)
    // applies on top of an editable contenteditable body. The
    // [NNNN] paragraph-number prefix is suppressed by the variant
    // — patent titles render as bare prose.
    const titleInner = (
      <div className="s6-paper" data-testid="spec-paper-title-line">
        {paragraphs.length === 0 ? (
            <div
              className="mt-8 flex flex-col items-center gap-3 rounded-sm border border-dashed border-zinc-300 bg-zinc-50 p-4 text-center"
              data-testid="spec-paper-empty-title"
            >
              <p className="text-sm text-zinc-700">Title not set yet.</p>
              <button
                type="button"
                disabled
                className="inline-flex h-8 items-center gap-1 rounded-sm border border-zinc-300 bg-white px-3 text-xs font-medium text-zinc-600 disabled:opacity-50"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Click Generate to draft this section
              </button>
            </div>
          ) : (
            <div className="mt-6">
              {paragraphs.map((p) => (
                <ParagraphBlock
                  key={p.id}
                  paragraph={p}
                  editable={isSectionEditable(section)}
                  verifyIssues={issuesByPara[p.id] ?? []}
                  variant="title"
                />
              ))}
            </div>
          )}
      </div>
    );
    if (embedded) return titleInner;
    return (
      <div
        className="s6-paper-backdrop flex flex-1 items-start justify-center overflow-y-auto px-6 py-12"
        data-testid="spec-paper-surface"
      >
        {titleInner}
      </div>
    );
  }

  const inner = (
    <div className="s6-paper">
        <div className="s6-paper-eyebrow">Section {seq}</div>
        <h2 className="s6-paper-heading mt-1">
          {/* BUG-048 — drafter rename overrides the seeded
              title. Empty/whitespace overrides fall back to the
              default so a half-typed rename can't blank the
              heading. */}
          {(section.custom_title && section.custom_title.trim())
            || section.title}
        </h2>

        {issues.length > 0 && (
          <div
            className="mt-3 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2"
            data-testid="spec-section-verify-banner"
            role="status"
          >
            <div className="flex items-center gap-2 text-[11px] font-medium text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5" />
              Verify · {issues.length} issue{issues.length === 1 ? '' : 's'}
              {score && score.completeness_pct !== undefined
                ? ` · ${score.completeness_pct}% complete`
                : ''}
            </div>
            {sectionScopedIssues.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-[11px] text-amber-900">
                {sectionScopedIssues.map((it, i) => (
                  <li key={i}>
                    <strong>{it.kind}:</strong> {it.detail}
                    {it.suggestion ? ` · ${it.suggestion}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* BUG-044 — cross-reference structured form. Renders
            above any existing paragraph(s) so the drafter can
            both edit the form fields AND see the rendered
            sentence below. Manual mode hides the form and lets
            the standard ParagraphBlock editor below handle
            free-form input. */}
        {isCrossRefSection && (
          <CrossReferenceForm
            matterId={matterId || ''}
            section={section}
            manualMode={crossRefManual}
            onToggleManual={() => setCrossRefManual((v) => !v)}
          />
        )}

        {section.is_auto_generated ? (
          <AutoGeneratedSection section={section} />
        ) : isCrossRefSection && paragraphs.length === 0 ? (
          /* BUG-044 — cross-reference's empty state IS the form
             above; no extra "Click Generate" stripe needed. */
          null
        ) : paragraphs.length === 0 ? (
          (section.section_kind === 'industrial_applicability'
            && (jurisdiction || '').toUpperCase() === 'US') ? (
            <div
              className="mt-8 flex flex-col items-start gap-3 rounded-sm border border-dashed border-zinc-300 bg-zinc-50 p-4"
              data-testid="spec-paper-us-skip-indapp"
            >
              <p className="text-sm font-medium text-zinc-700">
                Not applicable to US practice.
              </p>
              <p className="text-xs text-zinc-500">
                US patent applications don't require an industrial-applicability
                section. Click Generate to persist the documented skip
                placeholder, or leave the section empty — the export skips it
                automatically.
              </p>
            </div>
          ) : (
            <div
              className="mt-8 rounded-sm border border-dashed border-zinc-300 bg-zinc-50 p-4"
              data-testid="spec-paper-empty-section"
            >
              <p className="text-sm text-zinc-700">
                This section is empty. Click{' '}
                <strong className="font-semibold">Generate</strong>{' '}
                in the toolbar above to draft it.
              </p>
            </div>
          )
        ) : (
          <div className="mt-6">
            {paragraphs.map((p) => (
              <ParagraphBlock
                key={p.id}
                paragraph={p}
                editable={isSectionEditable(section)}
                verifyIssues={issuesByPara[p.id] ?? []}
              />
            ))}
          </div>
        )}
    </div>
  );
  if (embedded) return inner;
  return (
    <div
      className="s6-paper-backdrop flex flex-1 items-start justify-center overflow-y-auto px-6 py-12"
      data-testid="spec-paper-surface"
    >
      {inner}
    </div>
  );
}
